import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import BibleVerse from "@/components/BibleVerse";
import { translateScriptureReference } from "@/utils/bibleTranslations";

interface YouTubeVideo {
  id: string;
  title: string;
  youtube_url: string;
  description: string | null;
  created_at: string;
}

interface VideoTranslation {
  title: string;
  description: string | null;
}

export default function YouTubeVideos() {
  const { t, language } = useLanguage();
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [translations, setTranslations] = useState<Record<string, VideoTranslation>>({});
  const [loading, setLoading] = useState(true);

  const getYouTubeVideoId = (url: string): string => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : '';
  };

  const parseScriptureReferences = (description: string | null): string[] => {
    if (!description) return [];
    // Split by comma, semicolon, or newline, trim whitespace, and filter empty strings
    const refs = description.split(/[,;\n]/).map(ref => {
      // Remove bullet points and other leading special characters
      return ref.trim().replace(/^[•\-\*\s]+/, '').trim();
    }).filter(ref => ref.length > 0);
    // Translate each reference to the current language
    return refs.map(ref => translateScriptureReference(ref, language));
  };

  useEffect(() => {
    fetchVideos();
  }, [language]);

  const fetchVideos = async () => {
    try {
      const { data: videosData, error: videosError } = await supabase
        .from("youtube_videos")
        .select("*")
        .eq("published", true)
        .order("display_order", { ascending: true });

      if (videosError) throw videosError;
      setVideos(videosData || []);

      // Fetch translations for the current language
      if (videosData && videosData.length > 0) {
        const { data: translationsData, error: translationsError } = await supabase
          .from("youtube_videos_translations")
          .select("video_id, title, description")
          .eq("language", language)
          .in("video_id", videosData.map(v => v.id));

        if (!translationsError) {
          const translationsMap: Record<string, VideoTranslation> = {};
          if (translationsData && translationsData.length > 0) {
            translationsData.forEach(t => {
              translationsMap[t.video_id] = {
                title: t.title,
                description: t.description
              };
            });
          }
          setTranslations(translationsMap);
        }
      } else {
        setTranslations({});
      }
    } catch (error) {
      console.error("Error fetching videos:", error);
    } finally {
      setLoading(false);
    }
  };

  const getVideoContent = (video: YouTubeVideo) => {
    const translation = translations[video.id];
    return {
      title: translation?.title || video.title,
      description: translation?.description || video.description
    };
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="container mx-auto pt-24 pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">{t("youtube.page.title")}</h1>
            <p className="text-lg text-muted-foreground">
              {t("youtube.page.subtitle")}
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">{t("youtube.page.loading")}</div>
          ) : videos.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                {t("youtube.page.novideos")}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-8 md:grid-cols-2">
              {videos.map((video) => {
                const content = getVideoContent(video);
                return (
                  <Card key={video.id} className="overflow-hidden">
                    <CardHeader>
                      <CardTitle>{content.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {content.description && (
                        <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                          {parseScriptureReferences(content.description).map((ref, idx) => (
                            <BibleVerse key={idx} reference={ref} compact />
                          ))}
                        </div>
                      )}
                      <div className="aspect-video">
                        <iframe
                          width="100%"
                          height="100%"
                          src={`https://www.youtube.com/embed/${getYouTubeVideoId(video.youtube_url)}`}
                          title={content.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="rounded-lg"
                        />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
